import { IViewModelWithReactLifecycleHooks } from '../common/UIBindingForReact';
import { FeatureFlag, FeatureFlagService } from '../services/FeatureFlagService';
import { OmniboxV2ViewModel } from './OmniboxV2ViewModel';


export abstract class QuickSearchModule implements IViewModelWithReactLifecycleHooks {
    public isUnmounted = false;
    public omniboxViewModel: OmniboxV2ViewModel;
    public previousKeyboardNavigableModule: QuickSearchModule;
    public nextKeyboardNavigableModule: QuickSearchModule;
    public abstract getFirstKeyboardNavigableItem(): IKeyboardNavigableItem;
    public abstract getLastKeyboardNavigableItem(): IKeyboardNavigableItem;
    public abstract isKeyboardNavigable(): boolean;
    public abstract buildKeyboardNavigationPathInOwnModule(): void;
    public abstract selectItemByKeyboard(): void;
    public currentKeyboardNavigableItem?: IKeyboardNavigableItem;

    constructor(omniboxViewModel?: OmniboxV2ViewModel) {
        this.omniboxViewModel = omniboxViewModel;
    }

    public static buildKeyboardNavigationPath(source: QuickSearchModule, value: any) {
        source.resetActiveKeyboardNavigableItemIfItIsInThisModule();
        source.buildKeyboardNavigationPath();
    }

    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public initialize(): void {
    }

    public onMount() {
        this.isUnmounted = false;
        setTimeout(() => {
            this.resetActiveKeyboardNavigableItemIfItIsInThisModule();
            this.buildKeyboardNavigationPath();
        });
    }

    public onUnmount() {
        this.isUnmounted = true;

        setTimeout(() => {
            this.resetActiveKeyboardNavigableItemIfItIsInThisModule();
            this.buildKeyboardNavigationPath();
        });
    }

    public switchToNextItem(nextItem: IKeyboardNavigableItem) {
        if (nextItem) {
            if (this.omniboxViewModel.activeKeyboardNavigableItem) {
                this.omniboxViewModel.activeKeyboardNavigableItem.ownerModule.currentKeyboardNavigableItem = null;
            }
            this.omniboxViewModel.activeKeyboardNavigableItem = nextItem;
            nextItem.ownerModule.currentKeyboardNavigableItem = nextItem;
        }
    }

    public linkTwoKeyboardNavigableItems(item1: IKeyboardNavigableItem, item2: IKeyboardNavigableItem, isUpAndDown: boolean) {
        if (isUpAndDown) {
            item1.down = item2;
            item2.up = item1;
        } else {
            item1.right = item2;
            item2.left = item1;
        }
    }

    private buildKeyboardNavigationPath() {
        const omniboxViewModel = this.omniboxViewModel;

        if (omniboxViewModel.isKeyboardNavigable()) {
            // Step 1: build a module list, put all the navigable modules together.
            const modules: QuickSearchModule[] = [];
            modules.push(omniboxViewModel);

            if (omniboxViewModel.querySuggestionViewModel.isKeyboardNavigable()) {
                modules.push(omniboxViewModel.querySuggestionViewModel);
            }

            if (!omniboxViewModel.searchTerm) {
                // check if the quickLinksViewModel is navigable, then push it to modules
                if (omniboxViewModel.quickLinksViewModel.isKeyboardNavigable()) {
                    modules.push(omniboxViewModel.quickLinksViewModel);
                }
                if (FeatureFlagService.instance.getValue(FeatureFlag.smarterSearchEnabled) && omniboxViewModel.emptyStateContentSuggestionViewModel.isKeyboardNavigable()) {
                    modules.push(omniboxViewModel.emptyStateContentSuggestionViewModel);
                }
                if (omniboxViewModel.jumpBackInViewModel.enabled && omniboxViewModel.jumpBackInViewModel.isKeyboardNavigable()) {
                    modules.push(omniboxViewModel.jumpBackInViewModel);
                }
            } else {
                if (omniboxViewModel.contentSuggestionViewModel.isKeyboardNavigable()) {
                    modules.push(omniboxViewModel.contentSuggestionViewModel);
                }
            }

            // Step 2: link all the modules in the module list.
            modules.forEach((module, index) => {
                const nextModule = index < modules.length - 1 ? modules[index + 1] : modules[0];
                module.nextKeyboardNavigableModule = nextModule;
                nextModule.previousKeyboardNavigableModule = module;
            });

            // Step 3: let each module build its own navigation path.
            // Each module will build the path based on its own layout (vertical or grid or more)
            // Each module will figure out how to link its first and last keyboard navigable item to
            // its neighbour modules based on the information built in Step 2.
            modules.forEach(module => {
                module.buildKeyboardNavigationPathInOwnModule();
            });
        }
    }

    private resetActiveKeyboardNavigableItemIfItIsInThisModule() {
        if (this.omniboxViewModel.activeKeyboardNavigableItem === this.currentKeyboardNavigableItem) {
            this.switchToNextItem(this.omniboxViewModel.searchBoxNavigationItem);
        }
    }
}

export interface IKeyboardNavigableItem {
    up?: IKeyboardNavigableItem;
    down?: IKeyboardNavigableItem;
    left?: IKeyboardNavigableItem;
    right?: IKeyboardNavigableItem;
    ownerModule: QuickSearchModule;
    getIdentifier: () => string;
}